import { JSONSchema7 } from 'json-schema';
import { getByPointer } from 'json-pointer-relational';
import { IMAGE_SELECT_WIDGET, X_UI_WIDGET } from '../../utils/schemaExtensions';

export type QuoteSchema = {
  schema_type: string;
  schema_type_version?: number;
  schema: JSONSchema7;
};

export const recurseAndRemoveRequired = (schema: JSONSchema7): JSONSchema7 => {
  if (!schema || typeof schema !== 'object') return schema;

  const clone: JSONSchema7 = { ...schema };

  if (Array.isArray(clone.required)) {
    delete clone.required;
  }

  if (clone.properties && typeof clone.properties === 'object') {
    const nextProps: JSONSchema7['properties'] = {};
    for (const [key, value] of Object.entries(clone.properties)) {
      if (value && typeof value === 'object') {
        nextProps[key] = recurseAndRemoveRequired(value as JSONSchema7);
      } else {
        nextProps[key] = value as any;
      }
    }
    clone.properties = nextProps;
  }

  // Recurse into items (for array schemas)
  if (clone.items && typeof clone.items === 'object' && !Array.isArray(clone.items)) {
    clone.items = recurseAndRemoveRequired(clone.items as JSONSchema7);
  }

  return clone;
};

const resolveRef = (rootSchema: JSONSchema7, node: Record<string, any>): Record<string, any> => {
  if (node['$ref']) {
    const { $ref, ...rest } = node;
    return { ...rest, ...getByPointer($ref, rootSchema) };
  }
  return node;
};

const injectImageOptionsToProperties = (
  rootSchema: JSONSchema7,
  properties: Record<string, any>,
  enumOptions: (string | null)[]
): Record<string, any> => {
  const result: Record<string, any> = { ...properties };

  for (const [key, prop] of Object.entries(result)) {
    if (typeof prop !== 'object' || prop === null) continue;

    // Resolve $ref before checking type
    const p = resolveRef(rootSchema, prop) as JSONSchema7;

    // Recurse into nested objects
    if (p.type === 'object' && p.properties) {
      result[key] = {
        ...p,
        properties: injectImageOptionsToProperties(rootSchema, p.properties as Record<string, any>, enumOptions)
      };
      continue;
    }

    // Simple string field without existing enum
    if (p.type === 'string' && !p.enum) {
      // Replace null option with empty string
      result[key] = { ...p, enum: enumOptions.map(entry => entry === null ? "" : entry) };
      continue;
    }

    // Array field with string/null items
    if (p.type === 'array' && p.items) {
      // Handle both tuple items (array) and single items (object)
      const itemsList = Array.isArray(p.items) ? p.items : [p.items];
      const firstItem = resolveRef(rootSchema, itemsList[0] as Record<string, any>) as JSONSchema7;

      if ((firstItem.type === 'string' || firstItem.type?.includes?.('string')) && !firstItem.enum) {
        const enrichedItem = { ...firstItem, enum: enumOptions };
        result[key] = {
          ...p,
          items: Array.isArray(p.items)
            ? p.items.map(() => enrichedItem)
            : enrichedItem,
          [X_UI_WIDGET]: IMAGE_SELECT_WIDGET
        } as JSONSchema7;
      }
    }
  }

  return result;
};

export const injectImageOptionsToSchema = (
  schema: JSONSchema7,
  imageFiles: string[]
): JSONSchema7 => {
  if (!schema.properties) return schema;

  const enumOptions: (string | null)[] = [null, ...imageFiles];

  return {
    ...schema,
    properties: injectImageOptionsToProperties(schema, schema.properties as Record<string, any>, enumOptions)
  };
};

export const getLatestQuoteSchemas = (rawSchemas: any[]): QuoteSchema[] => {
  let list: QuoteSchema[] = (rawSchemas ?? []) as QuoteSchema[];

  // Filter out bad schema type from use
  list = list.filter((schema) => schema.schema_type !== 'QUOTE_EXPERIENCE_COLORS_CONTENT');

  const byType = list.reduce((acc, cur) => {
    const key = cur.schema_type;
    const existing = acc[key];
    const curVersion = cur.schema_type_version ?? 0;
    const existingVersion = existing?.schema_type_version ?? 0;

    if (!existing || curVersion > existingVersion) {
      acc[key] = cur;
    }

    return acc;
  }, {} as Record<string, QuoteSchema>);

  list = Object.values(byType).sort((a, b) =>
    a.schema_type.localeCompare(b.schema_type)
  );

  // Strip required props from images schema
  const imagesIdx = list.findIndex((entry) => entry.schema_type === 'QUOTE_EXPERIENCE_IMAGES');
  if (imagesIdx >= 0) {
    list[imagesIdx] = {
      ...list[imagesIdx],
      schema: recurseAndRemoveRequired(list[imagesIdx].schema),
    };
  }

  return list;
};
